import { useEffect, useState } from 'react';
import FeatureFlagModule from '../..';
import { FeatureFlagKeys } from '../../featureFlagKeys';

export interface UseFeatureFlagOptions<T> {
  defaultValue?: T;
}

/**
 * Hook to subscribe to a single feature flag value.
 * Automatically re-renders when the flag value changes.
 * If the flag has no value, returns {@link UseFeatureFlagOptions.defaultValue}
 * @param flagKey - The feature flag key to subscribe to
 * @param options - The options for the hook
 * @returns The current value of the feature flag, or {@link UseFeatureFlagOptions.defaultValue} if the flag has no value
 */
export function useFeatureFlag<T = boolean>(
  flagKey: FeatureFlagKeys,
  options: UseFeatureFlagOptions<T> & { defaultValue: T },
): T;

/**
 * Hook to subscribe to a single feature flag value.
 * Automatically re-renders when the flag value changes.
 * If the flag has no value, returns undefined (or {@link UseFeatureFlagOptions.defaultValue} if provided)
 * @param flagKey - The feature flag key to subscribe to
 * @param options - The options for the hook
 * @returns The current value of the feature flag, or undefined if the flag has no value
 * @example
 * const isTestFlagEnabled = useFeatureFlag('testFlag');
 * return (
 *   <div>
 *     <div>{isTestFlagEnabled ? 'Test flag is enabled' : 'Test flag is disabled'}</div>
 *   </div>
 * );
 */
export function useFeatureFlag<T = boolean>(
  flagKey: FeatureFlagKeys,
  options?: UseFeatureFlagOptions<T>,
): T | undefined;

export function useFeatureFlag<T = boolean>(
  flagKey: FeatureFlagKeys,
  options?: UseFeatureFlagOptions<T>,
): T | undefined {
  const { defaultValue } = options || {};
  const [{ value, hasValue }, setValue] = useState(() =>
    FeatureFlagModule.getFeatureFlagValue<T>(flagKey),
  );

  useEffect(() => {
    const { remove } = FeatureFlagModule.addFlagListener<T>(flagKey, setValue, {
      emitInitialValue: true,
    });

    return remove;
  }, [flagKey]);

  if (!hasValue) return defaultValue;
  return value;
}

export default useFeatureFlag;
